"use client"

import {DropdownMenuTrigger} from "@radix-ui/react-dropdown-menu"
import {EyeOpenIcon, EyeClosedIcon, MixerHorizontalIcon} from "@radix-ui/react-icons"
import {Table} from "@tanstack/react-table"

import {Button} from "../button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "../dropdown-menu"
import {ColumnConfig} from "./datatable";
import {Input} from "../input"
import {Checkbox} from "../checkbox"

interface DataTableViewOptionsProps<TData> {
    table: Table<TData>
    columnConfigs?: ColumnConfig[]
    setColumnConfigs?: React.Dispatch<React.SetStateAction<ColumnConfig[]>>
    isEditable?: boolean
}

export function DataTableViewOptions<TData>({
    table,
    columnConfigs,
    setColumnConfigs,
    isEditable
}: DataTableViewOptionsProps<TData>) {
    if (!isEditable) {
        return null
    }
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    size="sm"
                    className="ml-auto max-w-max hidden lg:flex h-8 text-textmedium bg-primarytransparent border border-primary rounded"
                >
                    <MixerHorizontalIcon className="mr-2 h-4 w-4"/>
                    Edit columns
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[350px] bg-background border rounded text-textmedium">
                <DropdownMenuLabel>Column Visibility Settings</DropdownMenuLabel>
                <DropdownMenuSeparator/>
                {table
                    .getAllColumns()
                    .filter(
                        (column) =>
                            typeof column.accessorFn !== "undefined" && column.getCanHide()
                    )
                    .map((column) => {
                        const columnConfig = columnConfigs?.find(
                            (config: ColumnConfig) => config.id === column.id
                        )
                        let currentTitle = columnConfig?.title ?? (column.columnDef.meta as { title?: string })?.title ?? ""
                        const currentRegex = columnConfig?.valueRegexExtractor ?? ""
                        const isVisible = column.getIsVisible()
                        const isFilterable = columnConfig?.isFilterable ?? false

                        return (
                            <div key={column.id} className="flex items-start gap-2 px-2 py-1.5">
                                <div className="flex-1 space-y-1">
                                    <div className="text-xs text-textmedium/60">
                                        {column.id}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2">
                                            <Input
                                                placeholder={column.id}
                                                className="h-7 text-sm"
                                                value={currentTitle}
                                                onChange={(e) => {
                                                    if (setColumnConfigs) {
                                                        setColumnConfigs((prev) => {
                                                            const newColumnConfigs = [...prev]
                                                            const columnConfig = newColumnConfigs.find(
                                                                (config: ColumnConfig) => config.id === column.id
                                                            )
                                                            if (columnConfig) {
                                                                columnConfig.title = e.target.value
                                                            } else {
                                                                newColumnConfigs.push({
                                                                    id: column.id,
                                                                    title: e.target.value,
                                                                })
                                                            }
                                                            return newColumnConfigs
                                                        })
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => {
                                                    if (setColumnConfigs) {
                                                        setColumnConfigs((prev) => {
                                                            const newColumnConfigs = [...prev]
                                                            const columnConfig = newColumnConfigs.find(
                                                                (config: ColumnConfig) => config.id === column.id
                                                            )
                                                            if (columnConfig) {
                                                                columnConfig.hiddenByDefault = isVisible
                                                            } else {
                                                                newColumnConfigs.push({
                                                                    id: column.id,
                                                                    hiddenByDefault: isVisible,
                                                                })
                                                            }
                                                            return newColumnConfigs
                                                        })
                                                    }
                                                }}
                                            >
                                                {isVisible ? (
                                                    <EyeOpenIcon className="h-4 w-4" />
                                                ) : (
                                                    <EyeClosedIcon className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                className="h-7 text-sm"
                                                placeholder="Regex to extract value (optional)"
                                                value={currentRegex}
                                                onChange={(e) => {
                                                    if (setColumnConfigs) {
                                                        setColumnConfigs((prev) => {
                                                            const newColumnConfigs = [...prev]
                                                            const columnConfig = newColumnConfigs.find(
                                                                (config: ColumnConfig) => config.id === column.id
                                                            )
                                                            if (columnConfig) {
                                                                columnConfig.valueRegexExtractor = e.target.value
                                                            } else {
                                                                newColumnConfigs.push({
                                                                    id: column.id,
                                                                    valueRegexExtractor: e.target.value,
                                                                })
                                                            }
                                                            return newColumnConfigs
                                                        })
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id={`filterable-${column.id}`}
                                                checked={isFilterable}
                                                onCheckedChange={(checked) => {
                                                    if (setColumnConfigs) {
                                                        setColumnConfigs((prev) => {
                                                            const newColumnConfigs = [...prev]
                                                            // First, unset isFilterable for all columns
                                                            newColumnConfigs.forEach(config => {
                                                                config.isFilterable = false
                                                            })
                                                            // Then set isFilterable for the selected column
                                                            const columnConfig = newColumnConfigs.find(
                                                                (config: ColumnConfig) => config.id === column.id
                                                            )
                                                            if (columnConfig) {
                                                                columnConfig.isFilterable = checked === true
                                                            } else {
                                                                newColumnConfigs.push({
                                                                    id: column.id,
                                                                    isFilterable: checked === true,
                                                                })
                                                            }
                                                            return newColumnConfigs
                                                        })
                                                    }
                                                }}
                                            />
                                            <label
                                                htmlFor={`filterable-${column.id}`}
                                                className="text-sm text-textmedium cursor-pointer"
                                            >
                                                Make this column filterable
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
